import m from "mithril";
import Requester from "../../shared/request";
import Gateway from "../../shared/app_settings";
import Toasts from "../../shared/toasts";
import i18n from "../../shared/i18n/i18n";
import Auth from "../../shared/auth";
import TextEditor from "../../shared/components/component_text_editor";

let saving = false;
let course = null;
let uploading;
let allCategories = [];         // All available: standard + custom
let selectedCategoryIds = [];   // IDs of custom categories currently selected
let categoriesLoaded = false;
let categoryDropdownOpen = false;
let categoryDropdownSearch = "";
let originalName = null;
let originalDescription = null;
let originalCategoryIds = [];
let isDirty = false;

function saveDetails() {
    const categoriesChanged = JSON.stringify(selectedCategoryIds.slice().sort()) !== JSON.stringify(originalCategoryIds.slice().sort());

    saving = true;

    const detailsChanged = course.name !== originalName || course.description !== originalDescription;
    const detailsRequest = detailsChanged
        ? Requester.post(Gateway.admin + "courses/" + course.id + "/edit-details", { Name: course.name, Description: course.description })
        : Promise.resolve();

    detailsRequest
    .then(function () {
        if (categoriesLoaded && allCategories.length > 0 && categoriesChanged) {
            return Requester.post(Gateway.admin + "courses/" + course.id + "/categories", { categoryIds: selectedCategoryIds });
        }
    })
    .then(function () {
        if (document.getElementById("course-image-file-input").files.length > 0)
            uploadImage();
        else if (!course.imagePath)
            Toasts.add("error", i18n.t("select_file"), "");
        else {
            Toasts.add("success", i18n.t("saved_changes"), "");
            originalName = course.name;
            originalDescription = course.description;
            originalCategoryIds = selectedCategoryIds.slice();
            isDirty = false;
        }
    })
    .catch(function (error) {
        Toasts.add("error", i18n.t("error"), i18n.t(error && error.response && error.response.properties && error.response.properties.errorCode || "unknown_error"));
    })
    .finally(function () {
        saving = false;
    });
}

function uploadImage() {
    const file = document.getElementById("course-image-file-input").files[0];
    uploading = true;

    const d = new FormData();

    if (!file) {
        Toasts.add("error", i18n.t("select_file"), "");
        return;
    }

    if (file.size / (1024 * 1024) > 3) {
        Toasts.add("error", i18n.t("file_too_big"), i18n.t("file_less_than") + "3MB");
        return;
    }

    d.append("file", file, file.name);

    Requester.upload(Gateway.admin + "courses/" + course.id + "/upload-image", d)
        .then(function () {
            Toasts.add("success", i18n.t("saved_changes"), "");
            course.imagePath = window.URL.createObjectURL(file);
            isDirty = false;
        })
        .catch(function (error) {
            if (error && error.response && error.response.errors && error.response.errors[""]) {
                const errorMessage = error.response.errors[""].join(", ");
                Toasts.add("error", i18n.t("error"), errorMessage);
            } else if (error && error.response && error.response.properties && error.response.properties.errorCode) {
                const errorCode = error.response.properties.errorCode;
                Toasts.add("error", i18n.t("error"), i18n.t(errorCode));
            } else {
                Toasts.add("error", i18n.t("error"), i18n.t("unknown_error"));
            }
        })
        .finally(function () {
            saving = false;
        });
}

function loadCategories() {
    categoriesLoaded = false;
    // Fetch all available categories (standard + custom) for the dropdown
    Requester.get(Gateway.admin + "courses/categories")
        .then(function (result) {
            allCategories = result.properties && result.properties.categories
                ? result.properties.categories
                : [];
            // Pre-select custom categories already assigned to this course
            const assignedCustom = (course.categories || []).filter(function (c) { return c.isEditable; });
            selectedCategoryIds = assignedCustom.map(function (c) { return c.id; });
            originalCategoryIds = selectedCategoryIds.slice();
        })
        .catch(function () {
            allCategories = [];
        })
        .finally(function () {
            categoriesLoaded = true;
        });
}

function toggleCategory(id) {
    const idx = selectedCategoryIds.indexOf(id);
    if (idx === -1) {
        selectedCategoryIds.push(id);
    } else {
        selectedCategoryIds.splice(idx, 1);
    }
    isDirty = true;
}

function isCategoryChecked(cat) {
    if (cat.isEditable) {
        return selectedCategoryIds.indexOf(cat.id) !== -1;
    }
    // Standard category: checked if the course has it (read-only)
    return (course.categories || []).some(function (c) { return !c.isEditable && c.name === cat.name; });
}

function buildSortedCategoryList() {
    const q = categoryDropdownSearch.toLowerCase();
    const standard = allCategories.filter(function (c) { return !c.isEditable; });
    const custom = allCategories.filter(function (c) { return c.isEditable; });

    function matchesSearch(cat) {
        if (!q) return true;
        return (cat.name || "").toLowerCase().indexOf(q) !== -1;
    }

    function sortCheckedAlpha(list, checkedFn) {
        const cmp = function (a, b) { return a.name.toLowerCase().localeCompare(b.name.toLowerCase()); };
        const checked = list.filter(checkedFn).sort(cmp);
        const unchecked = list.filter(function (c) { return !checkedFn(c); }).sort(cmp);
        return checked.concat(unchecked);
    }

    const sortedStandard = sortCheckedAlpha(
        standard.filter(matchesSearch),
        function (c) { return isCategoryChecked(c); }
    );
    const sortedCustom = sortCheckedAlpha(
        custom.filter(matchesSearch),
        function (c) { return isCategoryChecked(c); }
    );

    return { standard: sortedStandard, custom: sortedCustom };
}

const Component_Course_Details = {
    oninit: function (vnode) {
        course = vnode.attrs.course;
        allCategories = [];
        selectedCategoryIds = [];
        categoriesLoaded = false;
        categoryDropdownOpen = false;
        categoryDropdownSearch = "";
        originalName = course.name;
        originalDescription = course.description;
        originalCategoryIds = [];
        isDirty = false;
        loadCategories();
    },
    view: function () {
        return [
            m(".content-box", [
                m(".form", {class: !Auth.validateRights(["courses_edit_details"]) ? "disabled" : ""}, [
                    m(".form-section required", [
                        m(".form-label", i18n.t("name")),
                        m("input[type=text]", {
                            oninput: function () {
                                course.name = this.value;
                                isDirty = true;
                            }, value: course.name
                        })
                    ]),
                    m(".form-section", [
                        m(".form-label", i18n.t("version")),
                        m(".form-label", course.version)
                    ]),
                    m(".form-section required", [
                        m(".form-label", i18n.t("course_description"), m(".text-italic-course-description", i18n.t("appear"))),
                        m(TextEditor, {content: encodeURIComponent(course.description || ""), onchange: function (content) {
                            course.description = content;
                            isDirty = true;
                        }})

                    ]),
                    m(".form-section required", [
                        m(".form-label inline-tip", {"data-tip": "680x500px 3MB max"}, i18n.t("image")),
                        m("input[type=file].form-input#course-image-file-input", { onchange: function () { isDirty = true; m.redraw(); } }),
                        course.imagePath ? [
                            m("img.mt-1", {src: course.imagePath})
                        ] : ""
                    ]),
                    categoriesLoaded && allCategories.length > 0
                        ? m(".form-section", [
                            m(".form-label", i18n.t("categories")),
                            (function () {
                                // Build summary of selected categories (standard + custom) for the button label
                                const customCategories = allCategories.filter(function (c) { return c.isEditable; });
                                const checkedCustom = customCategories.filter(function (c) { return selectedCategoryIds.indexOf(c.id) !== -1; });
                                const checkedStandard = (course.categories || []).filter(function (c) { return !c.isEditable; });
                                const totalSelected = checkedCustom.length + checkedStandard.length;
                                const buttonLabel = totalSelected === 0
                                    ? i18n.t("select_categories")
                                    : totalSelected === 1
                                        ? (checkedCustom.length === 1
                                            ? m("span", { style: "overflow: hidden; white-space: nowrap; text-overflow: ellipsis; max-width: 150px; display: inline-block; vertical-align: middle;" }, checkedCustom[0].name)
                                            : i18n.t("category_" + (checkedStandard[0].name || "unassigned").toLowerCase().replace(/\s+/g, "_")))
                                        : totalSelected + " " + i18n.t("selected");

                                return m(".dropdown", { class: categoryDropdownOpen ? "open" : "" }, [
                                    m("button.with-icon pl-2", {
                                        type: "button",
                                        onclick: function (e) {
                                            e.stopPropagation();
                                            categoryDropdownOpen = !categoryDropdownOpen;
                                            if (categoryDropdownOpen) categoryDropdownSearch = "";
                                        }
                                    }, [
                                        buttonLabel,
                                        m("i.icon-back rotate-270 ml-2")
                                    ]),
                                    categoryDropdownOpen
                                        ? m(".dropdown-list", {
                                            style: "min-width: 260px; max-height: 320px; overflow-y: auto;",
                                            onclick: function (e) { e.stopPropagation(); },
                                            oncreate: function () {
                                                // Close on outside click
                                                document.addEventListener("click", function closeOnOutside() {
                                                    categoryDropdownOpen = false;
                                                    m.redraw();
                                                    document.removeEventListener("click", closeOnOutside);
                                                });
                                            }
                                        }, [
                                            // Search field
                                            m("div", { style: "padding: 0.4rem 0.5rem; border-bottom: 1px solid var(--border-color, #e5e7eb); position: sticky; top: 0; background: white; z-index: 1;" }, [
                                                m("input[type=text]", {
                                                    placeholder: i18n.t("search"),
                                                    value: categoryDropdownSearch,
                                                    oninput: function () { categoryDropdownSearch = this.value; },
                                                    onkeydown: function (e) {
                                                        if (e.key === "Escape") { categoryDropdownOpen = false; }
                                                        e.stopPropagation();
                                                    },
                                                    oncreate: function (vnode) { vnode.dom.focus(); },
                                                    style: "width: 100%; padding: 0.25rem 0.4rem; font-size: 0.875rem; border: 1px solid var(--border-color, #d1d5db); border-radius: 4px;"
                                                })
                                            ]),
                                            // Sorted category items
                                            (function () {
                                                const sorted = buildSortedCategoryList();
                                                const items = [];

                                                // Standard categories (read-only, shown for information)
                                                if (sorted.standard.length > 0) {
                                                    items.push(m(".dropdown-section-label", {
                                                        style: "padding: 0.35rem 0.8rem; font-size: 0.75rem; opacity: 0.6; cursor: default;"
                                                    }, i18n.t("standard_categories")));
                                                    sorted.standard.forEach(function (cat) {
                                                        const checked = isCategoryChecked(cat);
                                                        items.push(m("div", {
                                                            class: "flex align-center" + (checked ? " active" : ""),
                                                            style: "display: flex; align-items: center; cursor: default; opacity: 0.75;"
                                                        }, [
                                                            m("i", {
                                                                class: checked ? "icon-checkbox-checked mr-2" : "icon-checkbox-empty mr-2",
                                                                style: "flex-shrink: 0; line-height: 1;"
                                                            }),
                                                            m("span.dropdown-item-label", i18n.t("category_" + (cat.name || "unassigned").toLowerCase().replace(/\s+/g, "_")))
                                                        ]));
                                                    });
                                                }

                                                // Custom categories (interactive)
                                                if (sorted.custom.length > 0) {
                                                    items.push(m(".dropdown-section-label", {
                                                        style: "padding: 0.35rem 0.8rem; font-size: 0.75rem; opacity: 0.6; cursor: default;"
                                                    }, i18n.t("custom_course_categories")));
                                                    sorted.custom.forEach(function (cat) {
                                                        const checked = isCategoryChecked(cat);
                                                        items.push(m("div", {
                                                            class: "flex align-center c-hand" + (checked ? " active" : ""),
                                                            style: "display: flex; align-items: center;",
                                                            onclick: function () { toggleCategory(cat.id); }
                                                        }, [
                                                            m("i", {
                                                                class: checked ? "icon-checkbox-checked mr-2" : "icon-checkbox-empty mr-2",
                                                                style: "flex-shrink: 0; line-height: 1;"
                                                            }),
                                                            m("span.dropdown-item-label", { title: cat.name }, cat.name)
                                                        ]));
                                                    });
                                                }

                                                if (items.length === 0) {
                                                    return [m("div", { style: "padding: 0.5rem 0.8rem; font-size: 0.875rem; opacity: 0.6;" }, i18n.t("no_results"))];
                                                }
                                                return items;
                                            })()
                                        ])
                                        : null
                                ]);
                            })()
                        ])
                        : null,
                    m(".form-buttons", [
                        !saving ? m("button", {class: !isDirty ? "disabled" : "", onclick: saveDetails, disabled: !isDirty}, i18n.t("save_details")) : m("button.btn-text no-click", [i18n.t("saving"), m(".loading-dots")])
                    ])
                ])
            ])
        ];
    }
};

export default Component_Course_Details;
